import { useMemo, useRef, useState } from 'react';
import { placeLabel } from '../../utils/formatters';
import OpeningHoursEditor from './OpeningHoursEditor';
const behaviorTypeOptions = [
  {
    value: 'WALK_AND_WAIT',
    label: 'הליכה והמתנה',
  },
  {
    value: 'QUEUE_BASED',
    label: 'מבוסס תור',
  },
  {
    value: 'SEATED',
    label: 'ישיבה במקום',
  },
  {
    value: 'PASS_THROUGH',
    label: 'מעבר מהיר',
  },
  {
    value: 'UNKNOWN',
    label: 'לא ידוע',
  },
];
const localizedFields = [
  {
    key: 'name',
    label: 'שם',
  },
  {
    key: 'shortName',
    label: 'שם קצר',
  },
  {
    key: 'branchName',
    label: 'שם סניף',
  },
  {
    key: 'city',
    label: 'עיר',
  },
  {
    key: 'country',
    label: 'מדינה',
  },
  {
    key: 'address',
    label: 'כתובת',
  },
];
const defaultLocation = {
  lat: 32.0853,
  lng: 34.7818,
};
const tileSize = 256;
const mapZoom = 18;
const getLocation = (placeForm) => {
  const lat = Number(placeForm.lat);
  const lng = Number(placeForm.lng);
  return {
    lat: Number.isFinite(lat) ? lat : defaultLocation.lat,
    lng: Number.isFinite(lng) ? lng : defaultLocation.lng,
    hasValue: Number.isFinite(lat) && Number.isFinite(lng),
  };
};
const clampLat = (lat) => {
  return Math.max(Math.min(lat, 85.05112878), -85.05112878);
};
const lngLatToWorld = (lng, lat, zoom) => {
  const scale = tileSize * 2 ** zoom;
  const sinLat = Math.sin((clampLat(lat) * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  };
};
const worldToLngLat = (x, y, zoom) => {
  const scale = tileSize * 2 ** zoom;
  const lng = (x / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / scale;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return {
    lat,
    lng,
  };
};
const buildTiles = (center, zoom) => {
  const centerWorld = lngLatToWorld(center.lng, center.lat, zoom);
  const centerTileX = Math.floor(centerWorld.x / tileSize);
  const centerTileY = Math.floor(centerWorld.y / tileSize);
  const maxTile = 2 ** zoom;
  const tiles = [];
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      const tileX = centerTileX + dx;
      const tileY = centerTileY + dy;
      if (tileY < 0 || tileY >= maxTile) continue;
      const wrappedTileX = ((tileX % maxTile) + maxTile) % maxTile;
      tiles.push({
        key: `${wrappedTileX}-${tileY}`,
        src: `https://tile.openstreetmap.org/${zoom}/${wrappedTileX}/${tileY}.png`,
        x: tileX * tileSize - centerWorld.x,
        y: tileY * tileSize - centerWorld.y,
      });
    }
  }
  return tiles;
};
const getGoogleMapsUrl = (placeForm, location) => {
  if (!location.hasValue && !placeForm.googlePlaceId) return '';
  const coordinates = location.hasValue ? `${location.lat},${location.lng}` : '';
  const placeName = placeForm.name?.he || placeForm.name?.en || '';
  const query = placeName || coordinates;
  const params = new URLSearchParams({
    api: '1',
    query,
  });
  if (placeForm.googlePlaceId) params.set('query_place_id', placeForm.googlePlaceId);
  return `https://www.google.com/maps/search/?${params.toString()}`;
};
const LocationPicker = ({ placeForm, setPlaceForm }) => {
  const mapRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const location = getLocation(placeForm);
  const tiles = useMemo(() => buildTiles(location, mapZoom), [location]);
  const googleMapsUrl = getGoogleMapsUrl(placeForm, location);
  const updateFromPointer = (event) => {
    const rect = mapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const centerWorld = lngLatToWorld(location.lng, location.lat, mapZoom);
    const offsetX = event.clientX - rect.left - rect.width / 2;
    const offsetY = event.clientY - rect.top - rect.height / 2;
    const next = worldToLngLat(centerWorld.x + offsetX, centerWorld.y + offsetY, mapZoom);
    setPlaceForm((current) => ({
      ...current,
      lat: next.lat.toFixed(6),
      lng: next.lng.toFixed(6),
    }));
  };
  const handlePointerDown = (event) => {
    setDragging(true);
    updateFromPointer(event);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const handlePointerMove = (event) => {
    if (dragging) updateFromPointer(event);
  };
  const handlePointerUp = () => {
    setDragging(false);
  };
  return (
    <section className="editor-section location-section">
      <header className="editor-section-header">
        <h3>מיקום</h3>
        <span>
          {location.hasValue
            ? 'אפשר לגרור או ללחוץ על המפה כדי לעדכן Latitude / Longitude'
            : 'אין מיקום שמור, מוצג מרכז ברירת מחדל'}
        </span>
      </header>
      <div className="location-grid">
        <div
          className="map-box"
          ref={mapRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div className="tile-layer" aria-hidden="true">
            {tiles.map((tile) => (
              <img
                key={tile.key}
                src={tile.src}
                alt=""
                draggable="false"
                style={{
                  transform: `translate(${tile.x}px, ${tile.y}px)`,
                }}
              />
            ))}
          </div>
          <button
            type="button"
            className={`map-pin ${dragging ? 'dragging' : ''}`}
            aria-label="נקודת מיקום"
          />
        </div>
        <div className="location-fields">
          <label>
            Latitude
            <input
              dir="ltr"
              type="number"
              step="any"
              value={placeForm.lat}
              onChange={(e) =>
                setPlaceForm({
                  ...placeForm,
                  lat: e.target.value,
                })
              }
            />
          </label>
          <label>
            Longitude
            <input
              dir="ltr"
              type="number"
              step="any"
              value={placeForm.lng}
              onChange={(e) =>
                setPlaceForm({
                  ...placeForm,
                  lng: e.target.value,
                })
              }
            />
          </label>
          {googleMapsUrl && (
            <a
              className="button-link maps-link"
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              פתיחה ב-Google Maps
            </a>
          )}
        </div>
      </div>
    </section>
  );
};
const LanguageSection = ({ title, lang, placeForm, updateNested }) => {
  return (
    <section className="editor-section language-section">
      <header className="editor-section-header">
        <h3>{title}</h3>
      </header>
      <div className="language-grid">
        {localizedFields.map((field) => (
          <label key={field.key}>
            {field.label}
            <input
              value={placeForm[field.key][lang]}
              onChange={(e) => updateNested(field.key, lang, e.target.value)}
            />
          </label>
        ))}
      </div>
    </section>
  );
};
const PlaceDialog = ({
  open,
  selectedPlace,
  placeForm,
  setPlaceForm,
  updateNested,
  addRange,
  removeRange,
  updateRange,
  duplicateDay,
  duplicateTargets,
  setDuplicateTargets,
  setOpen247,
  isOpen247,
  onClose,
  onSave,
  loading,
}) => {
  if (!open || !selectedPlace) return null;
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <form
        className="modal"
        onSubmit={onSave}
        onMouseDown={(event) => event.stopPropagation()}
        dir="rtl"
      >
        <header className="modal-header">
          <div>
            <h2>{placeLabel(selectedPlace)}</h2>
            <span>{selectedPlace.placeId}</span>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            ×
          </button>
        </header>
        <section className="modal-body">
          <section className="editor-section">
            <header className="editor-section-header">
              <h3>פרטים כלליים</h3>
            </header>
            <div className="form-grid">
              <label>
                Google Place ID
                <input
                  disabled
                  value={placeForm.googlePlaceId}
                  onChange={(e) =>
                    setPlaceForm({
                      ...placeForm,
                      googlePlaceId: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                סטטוס
                <select
                  value={placeForm.status}
                  onChange={(e) =>
                    setPlaceForm({
                      ...placeForm,
                      status: e.target.value,
                    })
                  }
                >
                  <option value="active">active</option>
                  <option value="pending">pending</option>
                  <option value="rejected">rejected</option>
                </select>
              </label>
              <label>
                סוג התנהגות
                <select
                  value={placeForm.behaviorType}
                  onChange={(e) =>
                    setPlaceForm({
                      ...placeForm,
                      behaviorType: e.target.value,
                    })
                  }
                >
                  <option value="">בחר סוג התנהגות</option>
                  {behaviorTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                מקור
                <select
                  value={placeForm.source}
                  onChange={(e) =>
                    setPlaceForm({
                      ...placeForm,
                      source: e.target.value,
                    })
                  }
                >
                  <option value="system">system</option>
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                  <option value="google">google</option>
                </select>
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={placeForm.isVerified}
                  onChange={(e) =>
                    setPlaceForm({
                      ...placeForm,
                      isVerified: e.target.checked,
                    })
                  }
                />{' '}
                מאומת
              </label>
              <label>
                קטגוריות
                <input
                  value={placeForm.categoriesText}
                  onChange={(e) =>
                    setPlaceForm({
                      ...placeForm,
                      categoriesText: e.target.value,
                    })
                  }
                />
              </label>
            </div>
          </section>

          <div className="language-sections">
            <LanguageSection
              title="עברית"
              lang="he"
              placeForm={placeForm}
              updateNested={updateNested}
            />
            <LanguageSection
              title="אנגלית"
              lang="en"
              placeForm={placeForm}
              updateNested={updateNested}
            />
          </div>

          <LocationPicker placeForm={placeForm} setPlaceForm={setPlaceForm} />

          <OpeningHoursEditor
            days={placeForm.openingHours}
            addRange={addRange}
            removeRange={removeRange}
            updateRange={updateRange}
            duplicateDay={duplicateDay}
            duplicateTargets={duplicateTargets}
            setDuplicateTargets={setDuplicateTargets}
            setOpen247={setOpen247}
            isOpen247={isOpen247}
          />
        </section>
        <footer className="modal-footer">
          <button type="button" className="secondary" onClick={onClose}>
            ביטול
          </button>
          <button disabled={loading}>שמירה</button>
        </footer>
      </form>
    </div>
  );
};
export default PlaceDialog;
